// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React, { useCallback, useMemo } from 'react';
import { Card, Link, Typography } from '@kintent/glide';

import * as Styles from './DocumentsSection.styles';
import { Flex } from '../../../../../components/Flex';
import CustomTag from '../../../../../components/CustomTag/CustomTag';
import { useAuthService, useProgramContent, useTeamDocumentList } from '../../../../../lib/state';
import { getPublicDocuments, transformDocuments } from '../../../../../lib/utils';
import { RESOURCE_TYPE } from '../../../../../lib/constants';

function DocumentListItem({ artifact }) {
  const handleDocumentListItemClick = useCallback(() => {
    if (artifact.resourceType === RESOURCE_TYPE.LINK) {
      window.open(artifact.identifier, '_blank');
      return;
    }
    window.open(encodeURI(`/documents/${artifact.id}?type=${artifact.type}`), '_blank');
  }, [artifact]);
  return (
    <Card
      className="document-list-item"
      onClick={handleDocumentListItemClick}
    >
      <Flex
        direction="column"
        gap="12px"
      >
        <Typography
          as="p"
          level="6"
          className="document-list-item-title"
        >
          {artifact.title}
        </Typography>
        <Typography
          as="p"
          className="document-list-item-description"
          level="8"
          strength="60%"
        >
          {artifact.description}
        </Typography>
      </Flex>
    </Card>
  );
}

function DocumentsSection() {
  const { currentTeam } = useAuthService();
  const [documents] = useTeamDocumentList(currentTeam?.id);
  const [{ maxDocumentLimit }] = useProgramContent();

  const publicDocuments = useMemo(() => getPublicDocuments(documents), [documents]);

  const allPublicDocuments = useMemo(() => transformDocuments(publicDocuments, true), [publicDocuments]);

  const allPublicDocumentsToDisplay = allPublicDocuments.slice(0, maxDocumentLimit);

  return (
    <Styles.Section data-testid="document-section">
      <Typography
        as="h3"
        level="3"
        className="documents-section-title"
      >
        Comprehensive security and compliance resources
      </Typography>
      <Styles.DocumentCard>
        <Flex
          alignItems="center"
          gap="12px"
        >
          <CustomTag>{allPublicDocuments.length}</CustomTag>
          <Typography
            as="h5"
            level="5"
            className="documents-section-featured-items-title"
          >
            Featured Documents
          </Typography>
        </Flex>
        <Styles.DocumentCardGrid documentCardCount={maxDocumentLimit}>
          {allPublicDocumentsToDisplay.map((artifact) => {
            return (
              <DocumentListItem
                key={artifact.id}
                artifact={artifact}
              />
            );
          })}
        </Styles.DocumentCardGrid>
        <div className="show-all-documents-cta">
          <Typography
            as={Link}
            href="/documents"
          >
            Show All Documents
          </Typography>
        </div>
      </Styles.DocumentCard>
    </Styles.Section>
  );
}

export default DocumentsSection;
